import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import Faq from "../components/homi/Faq";
import ContactCta from "../components/homi/ContactCta";
import GalleryGrid from "../components/homi/GalleryGrid";
import { useContent } from "../context/ContentContext";
import { useContact } from "../context/ContentContext";
import { getIcon } from "../lib/icons";
import { slugify, getServiceDetail, getServiceGallery } from "../data/details";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { content } = useContent();
  const CONTACT = useContact();
  const services = content.services || [];
  const service = services.find((s) => slugify(s.title) === slug) || services[0];
  const detail = getServiceDetail(slug);
  const galleryImages = getServiceGallery(slug);
  const gallery = galleryImages.length ? galleryImages : (content.projects || []).slice(0, 4).map((p) => p.image);
  const Icon = getIcon(service?.icon);
  const quoteHref = `/homi/contact?service=${encodeURIComponent(service?.title || "")}`;

  return (
    <div className="App bg-white">
      <SiteHeader />
      <main>
        <PageBanner title={service?.title || "Service"} crumb={service?.title || "Service"} subtitle={service?.desc} />

        <section className="py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
            {/* Main */}
            <div className="lg:col-span-2">
              <div className="w-16 h-16 rounded-2xl bg-homi-gold/15 flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-homi-goldDark" />
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-homi-black mb-5">Professional {service?.title} Services</h2>
              {detail.longDesc.map((p) => (
                <p key={p.slice(0, 40)} className="text-homi-body leading-relaxed mb-4 text-lg">{p}</p>
              ))}

              <div className="grid sm:grid-cols-2 gap-4 my-8">
                {detail.benefits.map((b) => (
                  <div key={b} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-homi-gold/15 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-homi-goldDark" />
                    </span>
                    <span className="font-medium text-homi-black">{b}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-heading font-bold text-2xl text-homi-black mt-10 mb-5">Recent Work</h3>
              <GalleryGrid images={gallery} cols={2} />
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              <div className="bg-homi-light rounded-2xl p-6">
                <h4 className="font-heading font-bold text-lg text-homi-black mb-4">All Services</h4>
                <ul className="space-y-2">
                  {services.map((s) => {
                    const active = slugify(s.title) === slug;
                    return (
                      <li key={s.title}>
                        <Link to={`/homi/services/${slugify(s.title)}`}
                          className={`flex items-center justify-between px-4 py-3 rounded-lg font-heading font-semibold transition-colors ${active ? "bg-homi-gold text-homi-black" : "bg-white text-homi-black hover:bg-homi-gold/15"}`}>
                          {s.title} <ArrowRight className="w-4 h-4" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="bg-homi-black rounded-2xl p-8 text-center">
                <Phone className="w-10 h-10 text-homi-gold mx-auto mb-3" />
                <h4 className="font-heading font-bold text-xl text-white mb-2">Need this service?</h4>
                <p className="text-white/60 text-sm mb-5">Call us for a free quote today.</p>
                <a href={`tel:${CONTACT.phone}`} className="font-heading font-bold text-homi-gold text-lg">{CONTACT.phone}</a>
                <Link to={quoteHref} className="homi-btn-shine mt-5 block text-center bg-homi-gold hover:bg-homi-goldDark text-homi-black font-heading font-bold px-6 py-3 rounded-lg transition-colors">Get a Free Quote</Link>
              </div>
            </aside>
          </div>
        </section>

        <Faq />
        <ContactCta defaultService={service?.title} />
      </main>
      <Footer />
    </div>
  );
}
